import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  AtSignIcon,
  LockIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeOffIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "@/hooks/usePageTracking";
import { Capacitor } from "@capacitor/core";

import { crashlytics } from "../services/firebase";
const LoginPage: React.FC = () => {
  usePageTracking("Login", "LoginPage");
  const navigate = useNavigate();
  const {
    isLoading,
    setIsLoading,
    login,
    loginWithGoogle,
    loginWithApple,
    isAuthenticated,
    currentUser,
  } = useAuth();
  const platform = Capacitor.getPlatform();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle successful authentication
  useEffect(() => {
    crashlytics.log(
      `[LoginRedirect] isAuthenticated=${String(isAuthenticated)} userLoaded=${String(
        !!currentUser,
      )} hasCompletedOnboarding=${String(
        currentUser?.hasCompletedOnboarding,
      )}`,
    );

    // If we are authenticated but the user data is still null,
    // we stay in the loading state.
    if (isAuthenticated) {
      if (currentUser) {
        setIsLoading(false);
        const targetPath = currentUser.hasCompletedOnboarding
          ? "/"
          : "/profile-builder";
        crashlytics.log(`[LoginRedirect] navigating to ${targetPath}`);
        navigate(targetPath, { replace: true });
      } else {
        // Firebase Auth is signed in, but Firestore doc hasn't loaded yet
        crashlytics.log("[LoginRedirect] waiting for currentUser payload");
        setIsLoading(true);
      }
    }
  }, [isAuthenticated, currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      // Safety check: If after 5 seconds we are still loading,
      // it means the redirect hook didn't fire.
      setTimeout(() => {
        if (isLoading) setIsLoading(false);
      }, 5000);
    } catch (err) {
      crashlytics.logError(
        `Error during login: ${JSON.stringify(err?.message)}`,
        "LoginPage.tsx",
        err,
      );
      if (err instanceof Error) {
        // Enhanced error handling for common issues
        if (
          err.message.includes("auth/invalid-credential") ||
          err.message.includes("auth/user-not-found")
        ) {
          setError("Invalid email or password. Please try again.");
        } else if (err.message.includes("auth/network-request-failed")) {
          setError(
            "Network error. Please check your internet connection and try again.",
          );
        } else if (err.message.includes("auth/too-many-requests")) {
          setError("Too many failed attempts. Please try again later.");
        } else if (err.message.includes("auth/user-disabled")) {
          setError("This account has been disabled. Please contact support.");
        } else if (err.message.includes("auth/invalid-email")) {
          setError("Please enter a valid email address.");
        } else {
          setError(`Login failed: ${err.message}`);
        }
      } else {
        setError("An unknown error occurred.");
      }
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    // Don't set loading state for Google sign-in as it uses redirect
    // The redirect will happen immediately and loading overlay would block it
    try {
      await loginWithGoogle();
      // If we get here, redirect is in progress (no need to update UI)
    } catch (err: any) {
      crashlytics.logError(
        `Error during Google login: ${JSON.stringify(err?.message)}`,
        "LoginPage.tsx",
        err,
      );
      if (
        err.message.includes("popup-closed-by-user") ||
        err.message.includes("cancelled")
      ) {
        setError("Sign in was cancelled. Please try again.");
      } else {
        setError(
          err.message || "Failed to sign in with Google. Please try again.",
        );
      }
    }
  };

  const handleAppleLogin = async () => {
    setError("");
    try {
      await loginWithApple();
    } catch (err: any) {
      crashlytics.logError(
        `Error during Apple login: ${JSON.stringify(err?.message)}`,
        "LoginPage.tsx",
        err,
      );
      if (
        err.message.includes("popup-closed-by-user") ||
        err.message.includes("cancelled")
      ) {
        setError("Sign in was cancelled. Please try again.");
      } else {
        setError(
          err.message || "Failed to sign in with Apple. Please try again.",
        );
      }
    }
  };


  return (
    <div className="h-full bg-white text-gray-800 flex flex-col p-6">
      <header className="w-full max-w-sm mx-auto">
        <button
          onClick={() => navigate("/welcome")}
          className="text-gray-400 hover:text-gray-700 p-2 -ml-2"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
      </header>

      <main className="flex-1 flex flex-col justify-center items-center">
        <div className="w-full max-w-sm mx-auto animate-fade-in-zoom">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Welcome Back!
            </h1>
            <p className="text-gray-500 mt-2 leading-relaxed">
              Sign in to continue your journey.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <AtSignIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full bg-gray-100 border-gray-200 border rounded-full pl-12 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-800 placeholder-gray-500"
                required
              />
            </div>
            <div className="relative">
              <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-gray-100 border-gray-200 border rounded-full pl-12 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-800 placeholder-gray-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOffIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {error && (
              <p className="text-red-500 text-sm text-center font-semibold">
                {error}
              </p>
            )}

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-violet-600 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="w-full bg-violet-600 text-white font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-violet-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-violet-500/30"
              disabled={isLoading}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
            disabled={isLoading}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {isLoading ? "Signing In..." : "Continue with Google"}
          </button>

          {/* Apple Sign In is only available on iOS (native support) */}
          {platform === "ios" && (
            <button
              onClick={handleAppleLogin}
              className="w-full mt-3 bg-black text-white font-bold py-3.5 px-4 rounded-full disabled:opacity-60 hover:bg-gray-900 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
              disabled={isLoading}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {isLoading ? "Signing In..." : "Continue with Apple"}
            </button>
          )}

          <div className="text-center mt-6">
            <p className="text-sm text-gray-500">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-bold text-violet-600 hover:underline"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
